<template>
  <f7-page :page-content="true" name="festivos" @page:beforein="cargar">
    <f7-navbar :back-link="$t('atras')">
      <f7-nav-title sliding>{{ $t('festivos.titulo') }}</f7-nav-title>
      <f7-nav-right>
        <f7-link class="link-anio" @click="cambiarAnio(-1)">◀</f7-link>
        <span class="anio-actual">{{ anio }}</span>
        <f7-link class="link-anio" @click="cambiarAnio(1)">▶</f7-link>
        <f7-button
          v-if="puedeEditar"
          fill
          icon-f7="plus"
          :text="$t('festivos.nuevo')"
          class="display-flex margin-left"
          @click="abrirAlta()"
        ></f7-button>
        <!-- Publicar no escribe nada: abre la vista previa, y el que escribe es su botón. -->
        <f7-button
          v-if="puedeEditar"
          fill
          color="green"
          icon-f7="calendar_badge_plus"
          :text="$t('festivos.publicar')"
          class="display-flex margin-left"
          @click="abrirVistaPrevia()"
        ></f7-button>
      </f7-nav-right>
    </f7-navbar>

    <f7-block v-if="error403" strong>
      <p>{{ $t('festivos.noAutorizado') }}</p>
    </f7-block>

    <template v-else>
      <!-- Los contadores solo se pintan cuando se sabe lo que dicen. Mientras la tabla no ha
           llegado, un «Nacionales validados: 0/0» es la misma afirmación falsa que la lista
           vacía, contada dos veces: es lo que Dirección tuvo delante en producción el 06/10
           (#262). Y si la carga falló, tampoco hay cifras que dar. -->
      <f7-block v-if="!tablaCargada" strong class="contadores no-margin-bottom">
        <f7-preloader v-if="cargando" class="margin-right-half"></f7-preloader>
        <span class="contador contador-gris">
          {{ cargando ? $t('festivos.cargandoTabla') : $t('festivos.tablaSinCargar') }}
        </span>
      </f7-block>
      <f7-block v-else strong class="contadores no-margin-bottom">
        <span class="contador contador-gris">{{ $t('festivos.estados.NO_CARGADO') }}: {{ resumen.noCargados }}</span>
        <span class="contador contador-ambar">{{ $t('festivos.estados.PENDIENTE_VALIDAR') }}: {{ resumen.pendientesValidar }}</span>
        <span class="contador contador-granate">{{ $t('festivos.estados.FALTAN_COMPARTIDOS') }}: {{ resumen.faltanCompartidos }}</span>
        <span class="contador contador-rojo">{{ $t('festivos.estados.FALTAN_LOCALES') }}: {{ resumen.faltanLocales }}</span>
        <!-- Sobrar festivos es una anomalía de carga, no un estado por el que pase un municipio:
             el contador solo aparece cuando hay alguno. -->
        <span v-if="resumen.sobranCompartidos" class="contador contador-morado">
          {{ $t('festivos.estados.SOBRAN_COMPARTIDOS') }}: {{ resumen.sobranCompartidos }}
        </span>
        <span v-if="resumen.sobranLocales" class="contador contador-morado">
          {{ $t('festivos.estados.SOBRAN_LOCALES') }}: {{ resumen.sobranLocales }}
        </span>
        <span class="contador contador-verde">{{ $t('festivos.estados.VALIDADO') }}: {{ resumen.validados }}</span>
        <span v-if="resumen.publicados" class="contador contador-azul">
          {{ $t('festivos.publicados') }}: {{ resumen.publicados }}
        </span>
        <!-- Desactualizado no es un estado por el que pase un municipio, es algo que hay que
             volver a publicar: el contador solo aparece cuando hay alguno, como los de «sobran». -->
        <span v-if="resumen.desactualizados" class="contador contador-ambar">
          {{ $t('festivos.desactualizados') }}: {{ resumen.desactualizados }}
        </span>
        <span class="contador contador-total">
          {{ $t('festivos.nacionales') }}: {{ resumen.nacionalesValidados }}/{{ resumen.nacionales }}
        </span>
        <span v-if="resumen.personasSinMunicipio" class="contador contador-rojo">
          {{ $t('festivos.sinMunicipio') }}: {{ resumen.personasSinMunicipio }}
        </span>
        <!-- El recuento del respaldo (#255). Son insignias aparte de los estados del municipio
             porque miden otra cosa: no cómo está de cargado un calendario, sino si lo cargado
             cuadra con el boletín que dice citar. «Sin respaldo» sale siempre que la comprobación
             ha llegado —un 0 ahí es información— y las otras dos solo cuando hay algo, como las de
             «sobran». Y si la comprobación no llegó, se dice: lo que no se puede es callar y dejar
             que un 0 parezca que está todo bien. -->
        <template v-if="respaldoCargado">
          <span class="contador contador-granate">
            {{ $t('festivos.respaldo.sinRespaldo') }}: {{ resumenRespaldo.sinRespaldo }}
          </span>
          <span v-if="resumenRespaldo.sinComprobar" class="contador contador-gris">
            {{ $t('festivos.respaldo.sinComprobar') }}: {{ resumenRespaldo.sinComprobar }}
          </span>
          <span v-if="resumenRespaldo.noCargadosCuenta" class="contador contador-morado">
            {{ $t('festivos.respaldo.noCargados') }}: {{ resumenRespaldo.noCargadosCuenta }}
          </span>
        </template>
        <!-- Mientras la comprobación está en vuelo se dice, esté o no la cuenta anterior delante:
             sin esto, entre el clic y la respuesta no se pintaba nada y un catálogo sin comprobar
             se leía igual que uno comprobado y limpio. Y si ya hay cuenta, se queda —son los
             números de la última comprobación de verdad— pero no se da por vigente
             (revisión CODEX del PR #1799). -->
        <span v-if="respaldoComprobando" class="contador contador-gris">
          <f7-preloader class="margin-right-half"></f7-preloader>
          {{ $t('festivos.respaldo.comprobando') }}
        </span>
        <span v-else-if="respaldoSinCargar" class="contador contador-gris">
          {{ $t('festivos.respaldo.sinCargar') }}
          <f7-link class="reintentar-respaldo" @click="cargarRespaldo()">
            {{ $t('festivos.reintentar') }}
          </f7-link>
        </span>
      </f7-block>

      <!-- #1210 · El desglose del aviso «calendario sin cerrar», por motivo. Va debajo de los
           contadores y plegado salvo si se llega desde el indicador de la cabecera: la tabla y la
           ficha del municipio siguen donde estaban. -->
      <festivos-avisos ref="avisos" :abrir-de-entrada="vieneDelIndicador"></festivos-avisos>

      <f7-row no-gap>
        <f7-col width="100" medium="45">
          <f7-block-title>{{ $t('festivos.municipiosEnUso') }}</f7-block-title>
          <f7-list no-hairlines class="no-margin-vertical">
            <f7-list-input
              type="text"
              :placeholder="$t('festivos.buscarMunicipio')"
              :value="filtro"
              clear-button
              @input="filtro = ($event.target.value || '')"
              @input:clear="filtro = ''"
            >
              <f7-icon slot="media" f7="search" size="18"></f7-icon>
            </f7-list-input>
          </f7-list>
          <div class="tabla-municipios">
            <div class="fila cabecera">
              <span class="col-municipio">{{ $t('festivos.municipio') }}</span>
              <span class="col-num">{{ $t('festivos.personas') }}</span>
              <span class="col-num">{{ $t('festivos.nac') }}</span>
              <span class="col-num">{{ $t('festivos.aut') }}</span>
              <span class="col-num">{{ $t('festivos.loc') }}</span>
              <span class="col-estado">{{ $t('festivos.estado') }}</span>
            </div>
            <div
              v-for="m in municipiosFiltrados"
              :key="m.municipioIne"
              class="fila"
              :class="{ seleccionada: ineSeleccionado == m.municipioIne }"
              @click="seleccionar(m.municipioIne)"
            >
              <span class="col-municipio">{{ m.municipioNombre }} <small>({{ m.municipioIne }})</small></span>
              <span class="col-num">{{ m.personas }}</span>
              <span class="col-num">{{ m.nacionales }}</span>
              <span class="col-num">{{ m.autonomicos }}</span>
              <span class="col-num">{{ m.localesValidados }}/{{ m.locales }}</span>
              <span class="col-estado">
                <span class="chip-estado" :class="'chip-' + m.estado">{{ $t('festivos.estados.' + m.estado) }}</span>
              </span>
            </div>
            <!-- Tres estados distintos y ninguno se puede confundir con otro (#262):
                 CARGANDO   mientras la petición está en vuelo y no hay nada que enseñar todavía.
                            Faltaba, y era el defecto: `tablaSinCargar` arranca en false, así que
                            lo que se pintaba era el mensaje de «vacío» y la pantalla afirmaba que
                            nadie de la plantilla tiene municipio sin haberlo comprobado. Con un
                            GET de 90 s eso lo tuvo Dirección delante minuto y medio.
                 ERROR      la petición falló. Lleva reintento: sin tabla que respalde lo que se
                            ve, operar desde ahí sería hacerlo a ciegas (PR #1709).
                 VACÍO      ya contestó y no hay nada. Solo aquí se puede decir.
                 Y el vacío son dos cosas, no una: que no haya nadie con municipio, o que no haya
                 ninguno que case con lo que se ha escrito en el buscador. «Nadie de la plantilla
                 tiene municipio» al buscar BRUNETE es otra afirmación que la pantalla no ha
                 comprobado. -->
            <div v-if="!tablaCargada && cargando" class="fila vacia">
              <f7-preloader class="margin-right-half"></f7-preloader>{{ $t('festivos.cargandoTabla') }}
            </div>
            <div v-else-if="tablaSinCargar" class="fila vacia">
              {{ $t('festivos.tablaSinCargar') }}
              <f7-link class="margin-left-half" @click="cargar()">{{ $t('festivos.reintentar') }}</f7-link>
            </div>
            <div v-else-if="!municipiosFiltrados.length" class="fila vacia">
              {{ filtroNormalizado ? $t('festivos.sinCoincidencias') : $t('festivos.sinMunicipios') }}
            </div>
          </div>
        </f7-col>

        <f7-col width="100" medium="55">
          <f7-block v-if="!seleccionado" strong>
            <p>{{ $t('festivos.seleccioneMunicipio') }}</p>
          </f7-block>
          <template v-else>
            <f7-card class="padding-vertical-half">
              <f7-card-header>
                {{ seleccionado.municipioNombre }} ({{ seleccionado.municipioIne }})
                <span class="chip-estado" :class="'chip-' + seleccionado.estado">
                  {{ $t('festivos.estados.' + seleccionado.estado) }}
                </span>
              </f7-card-header>
              <f7-card-content>
                <!-- Por qué está desactualizado no se puede decir —lo que cambió pudo ser el
                     maestro de municipios, SAP o un festivo que se movió de INE—, así que se dice
                     qué hacer: mirar la vista previa y volver a publicar. No bloquea nada. -->
                <p v-if="seleccionado.estado === 'PUBLICADO_DESACTUALIZADO'" class="aviso-desactualizado">
                  {{ $t('festivos.desactualizadoExplicacion') }}
                </p>
                <p v-if="seleccionado.validadoPor" class="pie-validacion">
                  {{ $t('festivos.validadoPor') }} <b>{{ seleccionado.validadoPor }}</b>
                  <span v-if="seleccionado.fechaValidacion"> · {{ seleccionado.fechaValidacion | dateFormat('DD/MM/YYYY') }}</span>
                </p>
                <p v-if="seleccionado.completoConLocales != null" class="pie-validacion">
                  {{ $t('festivos.completoCon') }} {{ seleccionado.completoConLocales }}
                  · {{ seleccionado.motivoCompletoLocales }}
                  <!-- El marcado vale para los locales que se dieron por buenos; si después
                       cambian, el municipio vuelve a «faltan locales» y hay que volver a mirarlo. -->
                  <b v-if="completoDesfasado" class="aviso-desfasado">· {{ $t('festivos.completoDesfasado') }}</b>
                </p>

                <!-- Sin comunidad no hay autonómicos que pintar y el calendario no puede cerrarse
                     nunca. Se dice aquí, que es donde está el botón de cargarle un festivo local,
                     porque es ahí donde se arregla (revisión CODEX del PR #1941). -->
                <p v-if="!seleccionado.codComunidad" class="aviso-sin-comunidad">
                  {{ $t('festivos.sinComunidad') }}
                </p>

                <!-- Todo lo que le falla, no solo lo que cabe en la chapa, y JUNTO a los botones
                     que lo arreglan. Cada línea dice qué pasa con sus números, qué hay que hacer y
                     con qué botón; la chapa se queda con lo más grave y antes callaba el resto, así
                     que un municipio al que le faltaba un nacional y le sobraban locales solo
                     enseñaba una de las dos cosas y «Validar» no arreglaba ninguna. -->
                <div v-if="queFalta.length" class="que-falta">
                  <p class="que-falta-titulo">{{ $t('festivos.loQueFalta') }}</p>
                  <ul>
                    <li v-for="linea in queFalta" :key="linea.codigo">
                      <b>{{ $t('festivos.estados.' + linea.codigo) }}</b> · {{ linea.texto }}
                    </li>
                  </ul>
                  <!-- Y el otro eje: lo de arriba es del catálogo y no deshace lo publicado. Solo
                       aquí, porque cuando el catálogo cuadra eso ya lo dice la chapa. -->
                  <p v-if="textoPublicacion" class="que-falta-publicacion">{{ textoPublicacion }}</p>
                </div>

                <div class="acciones" v-if="puedeEditar">
                  <!-- Deshabilitado cuando no tiene nada que validar: antes se podía pulsar, la
                       petición volvía sin error, la pantalla recargaba y no cambiaba nada ni se
                       decía por qué. El motivo va pegado al botón, no en un aviso de arriba. -->
                  <f7-button
                    small
                    fill
                    color="green"
                    :disabled="!seleccionado.sinValidar && !!seleccionado.validadoPor"
                    @click="validar"
                  >{{ $t('festivos.validar') }}</f7-button>
                  <f7-button
                    v-if="seleccionado.completoConLocales == null || completoDesfasado"
                    small
                    color="orange"
                    @click="marcarCompleto(false)"
                  >{{ $t('festivos.marcarCompleto') }}</f7-button>
                  <f7-button
                    v-if="seleccionado.completoConLocales != null"
                    small
                    color="gray"
                    @click="marcarCompleto(true)"
                  >{{ $t('festivos.deshacerCompleto') }}</f7-button>
                  <f7-button small @click="abrirAlta(seleccionado.municipioIne)">{{ $t('festivos.nuevoLocal') }}</f7-button>
                </div>
                <!-- Por qué «Validar» está apagado, quién lo desbloquea y dónde: aquí no hace
                     falta nadie más —lo desbloquea un festivo que falta por cargar, y el botón
                     para cargarlo está justo arriba—, y eso es lo que dice la línea. -->
                <p v-if="puedeEditar && !seleccionado.sinValidar && seleccionado.validadoPor" class="motivo-deshabilitado">
                  {{ $t('festivos.validarSinNadaPendiente', { validados: festivosQueAplican }) }}
                </p>

                <f7-list class="no-margin-vertical lista-festivos">
                  <f7-list-item v-if="!festivos.length" :title="$t('festivos.sinFestivos')"></f7-list-item>
                  <f7-list-item
                    v-for="f in festivos"
                    :key="f.id"
                    :class="{ 'festivo-no-aplica': !!f.noAplicaAqui }"
                    :title="f.fecha + ' · ' + f.nombre"
                    :after="$t('festivos.ambitos.' + f.ambito)"
                  >
                    <!-- «¿Genera tarea?» es del festivo, no del municipio: uno en sábado o
                         domingo, o todavía en PROPUESTO, no llega a ningún calendario. Lo decide
                         el backend y aquí solo se pinta. -->
                    <span
                      slot="subtitle"
                      class="genera-tarea"
                      :class="f.generaTarea ? 'genera-si' : 'genera-no'"
                    >
                      {{ $t('festivos.generaTarea') }}
                      {{ f.generaTarea ? $t('festivos.generaTareaSi') : $t('festivos.generaTareaNo') }}
                    </span>
                    <f7-icon
                      slot="media"
                      size="14"
                      :f7="f.estado === 'VALIDADO' ? 'checkmark_seal_fill' : 'clock'"
                      :color="f.estado === 'VALIDADO' ? 'green' : 'orange'"
                    ></f7-icon>
                    <!-- Un nacional que su comunidad no aplica no lleva lápiz ni papelera: los dos
                         actúan sobre el registro del BOE, que es de todos los municipios, y aquí
                         parecerían quitarle a este municipio algo que ya no tiene. Lo único que se
                         puede hacer desde aquí con esa fila es deshacer la marca (#1148, §9). -->
                    <template slot="after-title" v-if="puedeEditar && !f.noAplicaAqui">
                      <f7-link icon-f7="pencil" class="accion-fila" @click.stop="abrirEdicion(f)"></f7-link>
                      <f7-link icon-f7="trash" class="accion-fila" @click.stop="borrar(f)"></f7-link>
                    </template>
                    <!-- El alta manual admite guardar solo el enlace, sin referencia del boletín;
                         diciendo «Sin fuente» ahí se perdía de la pantalla la única pista de
                         dónde salió el festivo (revisión CODEX del PR #1709). -->
                    <div slot="footer">
                      <a
                        v-if="enlaceFuente(f)"
                        :href="enlaceFuente(f)"
                        target="_blank"
                        rel="noopener"
                        class="external"
                      >{{ f.fuenteRef || f.fuenteUrl }}</a>
                      <span v-else>{{ f.fuenteRef || f.fuenteUrl || $t('festivos.sinFuente') }}</span>
                      <span v-if="f.validadoPor"> · {{ f.validadoPor }}</span>
                      <!-- Junto a los botones y no en un aviso de arriba: el lápiz y la papelera
                           de esta fila tocan el registro compartido, y quien los pulsa tiene que
                           ver antes que no es solo de este municipio (#1148). -->
                      <span v-if="f.municipiosQueLoComparten != null && !f.noAplicaAqui" class="compartido">
                        · {{ $t('festivos.compartido.fila', {
                          municipios: f.municipiosQueLoComparten, personas: f.personasQueLoComparten || 0 }) }}
                      </span>
                    </div>
                    <!-- El nacional que su comunidad no aplica se enseña, no se esconde: así se ve
                         de dónde sale la marca y se puede deshacer desde aquí (#1148). -->
                    <div v-if="f.noAplicaAqui" slot="root-end" class="aviso-no-aplica">
                      <b>{{ $t('festivos.noAplica.marca', {
                        comunidad: f.noAplicaAqui.comunidadNombre || f.noAplicaAqui.codComunidad,
                        fuente: f.noAplicaAqui.fuenteRef || f.noAplicaAqui.fuenteUrl }) }}</b>
                      · {{ f.noAplicaAqui.motivo }}
                      <span v-if="f.noAplicaAqui.excluidoPor"> · {{ f.noAplicaAqui.excluidoPor }}</span>
                      <br />
                      {{ $t('festivos.noAplica.noCuenta') }}
                      <f7-link
                        v-if="puedeEditar"
                        class="accion-no-aplica"
                        @click.stop="volverAAplicar(f)"
                      >{{ $t('festivos.noAplica.deshacer') }}</f7-link>
                    </div>
                    <!-- Y al revés: un nacional que sí le toca se puede marcar como que esta
                         comunidad no lo aplica, sin borrarlo para nadie más. Sin comunidad
                         conocida no se dibuja: no hay a quién aplicárselo, y el aviso de arriba
                         («No se sabe a qué comunidad pertenece…») ya dice cómo se arregla. -->
                    <div
                      v-else-if="puedeEditar && f.ambito === 'NACIONAL' && seleccionado.codComunidad"
                      slot="root-end"
                      class="accion-no-aplica-fila"
                    >
                      <f7-link class="accion-no-aplica" @click.stop="abrirNoAplica(f)">
                        {{ $t('festivos.noAplica.boton', { comunidad: nombreComunidad(seleccionado.codComunidad) }) }}
                      </f7-link>
                    </div>
                    <!-- El aviso de respaldo (#255), junto a la fila del festivo y con el por qué
                         dentro: quien lo lee tiene que saber qué le pasa a ESTE festivo sin ir a
                         ningún otro sitio (§9). Va en `root-end` y no en el pie de la fila porque
                         es un párrafo entero: el pie de Framework7 es de una línea y lo recortaría.
                         No deshabilita ni esconde nada: el festivo se sigue pudiendo corregir,
                         validar y publicar igual. -->
                    <div
                      v-if="avisoPorFestivo[f.id]"
                      slot="root-end"
                      class="aviso-respaldo"
                      :class="'aviso-' + avisoPorFestivo[f.id].tipo"
                    >
                      <b>{{ $t('festivos.respaldo.etiquetas.' + avisoPorFestivo[f.id].tipo) }}</b>
                      · {{ avisoPorFestivo[f.id].motivo }}
                    </div>
                  </f7-list-item>
                </f7-list>

                <!-- Dicho una vez por municipio y no en cada fila: los avisos de respaldo no
                     bloquean nada. Repetirlo en cada aviso solo taparía el por qué de cada uno. -->
                <p v-if="avisosDelMunicipio" class="nota-respaldo">
                  {{ $t('festivos.respaldo.noBloquea') }}
                </p>

                <!-- Y el sentido contrario: lo que el boletín leído trae para este municipio y no
                     está en el catálogo. Va debajo de la lista porque no es una fila del catálogo
                     —todavía no existe— y no se da de alta solo: lo decide RRHH. -->
                <div v-if="faltanDelBoletin.length" class="bloque-faltan-boletin">
                  <p class="titulo-faltan">{{ $t('festivos.respaldo.tituloNoCargados') }}</p>
                  <p v-for="(c, i) in faltanDelBoletin" :key="'nc' + i" class="linea-faltan">
                    <b>{{ c.fecha }} · {{ nombreDelAviso(c) }}</b>
                    <span class="ambito-faltan">{{ $t('festivos.ambitos.' + c.ambito) }}</span>
                    <br />
                    {{ c.motivo }}
                    <span v-if="c.textoBoletin" class="texto-boletin">«{{ c.textoBoletin }}»</span>
                  </p>
                </div>
              </f7-card-content>
            </f7-card>
          </template>
        </f7-col>
      </f7-row>
    </template>

    <!-- Alta y corrección manual. Popup propio y no r-form: son cuatro campos y el guardado va
         a su endpoint, no al CRUD genérico. -->
    <f7-popup :opened="popupAbierto" @popup:closed="popupAbierto = false">
      <f7-page>
        <f7-navbar :title="edicion.id ? $t('festivos.editar') : $t('festivos.nuevo')">
          <f7-nav-right>
            <f7-link popup-close>{{ $t('cerrar') }}</f7-link>
          </f7-nav-right>
        </f7-navbar>
        <f7-list no-hairlines-md>
          <!-- Acotado al año que se está mirando: ni el alta ni la corrección mandan el año por
               separado, así que una fecha de 2027 guardada desde la cabecera de 2026 movía el
               festivo al año siguiente y desaparecía de la tabla al recargar. -->
          <f7-list-input
            :label="$t('festivos.fecha')"
            type="date"
            :value="edicion.fecha"
            :min="anio + '-01-01'"
            :max="anio + '-12-31'"
            @input="edicion.fecha = $event.target.value"
          ></f7-list-input>
          <f7-list-input
            :label="$t('festivos.nombre')"
            type="text"
            :value="edicion.nombre"
            @input="edicion.nombre = $event.target.value"
          ></f7-list-input>
          <f7-list-input
            :label="$t('festivos.ambito')"
            type="select"
            :value="edicion.ambito"
            @change="edicion.ambito = $event.target.value"
          >
            <option value="NACIONAL">{{ $t('festivos.ambitos.NACIONAL') }}</option>
            <option value="AUTONOMICO">{{ $t('festivos.ambitos.AUTONOMICO') }}</option>
            <option value="LOCAL">{{ $t('festivos.ambitos.LOCAL') }}</option>
          </f7-list-input>
          <!-- La comunidad se elige del catálogo, no se teclea: el catálogo de festivos reparte
               los autonómicos por coincidencia exacta con el código, y una errata crearía un
               festivo que no le llega a ningún municipio (el backend lo rechaza igual). -->
          <f7-list-input
            v-if="edicion.ambito === 'AUTONOMICO'"
            :label="$t('festivos.comunidad')"
            type="select"
            :value="edicion.codComunidad || ''"
            @change="edicion.codComunidad = $event.target.value"
          >
            <option value="">{{ $t('festivos.seleccioneComunidad') }}</option>
            <option v-for="c in opcionesComunidad" :key="c.codigo" :value="c.codigo">
              {{ c.codigo }} · {{ c.nombre }}
            </option>
          </f7-list-input>
          <f7-list-input
            v-if="edicion.ambito === 'LOCAL'"
            :label="$t('festivos.municipioIne')"
            type="text"
            :value="edicion.municipioIne"
            @input="cambiarMunicipioIne($event.target.value)"
          ></f7-list-input>
          <!-- Solo para el municipio cuya comunidad NO fija el maestro: ahí la del formulario es
               la mejor fuente que hay —o la única, si nadie ha cargado todavía un local suyo—.
               Cuando el maestro sí la dice, este campo no sale porque la comunidad de un municipio
               no la elige quien carga el festivo.
               Que salga también cuando ya hay una comunidad deducida es a propósito: esa deducción
               sale de sus propios festivos, así que cargado el primero con la comunidad
               equivocada no había ninguna pantalla donde corregirla (revisión CODEX del PR #1941). -->
          <f7-list-input
            v-if="comunidadLocalEditable"
            :label="$t('festivos.comunidadLocal')"
            type="select"
            :info="municipioDeLaEdicion && municipioDeLaEdicion.codComunidad ? $t('festivos.comunidadLocalAviso') : ''"
            :value="edicion.codComunidad || ''"
            @change="edicion.codComunidad = $event.target.value"
          >
            <option value="">{{ $t('festivos.seleccioneComunidad') }}</option>
            <option v-for="c in opcionesComunidad" :key="c.codigo" :value="c.codigo">
              {{ c.codigo }} · {{ c.nombre }}
            </option>
          </f7-list-input>
          <f7-list-input
            :label="$t('festivos.fuenteRef')"
            type="text"
            :placeholder="'BOE-A-2025-21667'"
            :value="edicion.fuenteRef"
            @input="edicion.fuenteRef = $event.target.value"
          ></f7-list-input>
          <f7-list-input
            :label="$t('festivos.fuenteUrl')"
            type="text"
            :value="edicion.fuenteUrl"
            @input="edicion.fuenteUrl = $event.target.value"
          ></f7-list-input>
          <f7-list-input
            :label="$t('festivos.motivo')"
            type="text"
            :value="edicion.motivo"
            @input="edicion.motivo = $event.target.value"
          ></f7-list-input>
        </f7-list>
        <f7-block>
          <!-- Encima del botón que lo hace, no en la ficha de detrás: la corrección de un
               nacional o un autonómico le llega a todos los que lo comparten (#1148). -->
          <p v-if="avisoCompartidoEdicion" class="aviso-compartido">{{ avisoCompartidoEdicion }}</p>
          <f7-button fill @click="guardarFestivo">{{ $t('festivos.guardar') }}</f7-button>
        </f7-block>
      </f7-page>
    </f7-popup>

    <!-- «No aplica en esta comunidad» de un nacional (#1148). Popup propio porque son tres campos
         y el motivo y la fuente son obligatorios: un prompt solo admite uno. -->
    <f7-popup :opened="noAplica.abierto" @popup:closed="noAplica.abierto = false">
      <f7-page>
        <f7-navbar :title="$t('festivos.noAplica.titulo')">
          <f7-nav-right>
            <f7-link popup-close>{{ $t('cerrar') }}</f7-link>
          </f7-nav-right>
        </f7-navbar>
        <f7-block v-if="noAplica.festivo">
          <p>{{ $t('festivos.noAplica.explicacion', {
            festivo: noAplica.festivo.fecha + ' · ' + noAplica.festivo.nombre,
            comunidad: nombreComunidad(noAplica.codComunidad) }) }}</p>
        </f7-block>
        <f7-list no-hairlines-md>
          <f7-list-input
            :label="$t('festivos.noAplica.motivo')"
            type="text"
            :placeholder="$t('festivos.noAplica.motivoEjemplo')"
            :value="noAplica.motivo"
            @input="noAplica.motivo = $event.target.value"
          ></f7-list-input>
          <f7-list-input
            :label="$t('festivos.fuenteRef')"
            type="text"
            :placeholder="'Decreto 100/2025 (DOGV 10145)'"
            :value="noAplica.fuenteRef"
            @input="noAplica.fuenteRef = $event.target.value"
          ></f7-list-input>
          <f7-list-input
            :label="$t('festivos.fuenteUrl')"
            type="text"
            :value="noAplica.fuenteUrl"
            @input="noAplica.fuenteUrl = $event.target.value"
          ></f7-list-input>
        </f7-list>
        <f7-block>
          <f7-button fill @click="guardarNoAplica">{{ $t('festivos.noAplica.guardar') }}</f7-button>
        </f7-block>
      </f7-page>
    </f7-popup>

    <!-- Vista previa de la publicación. Lo que se ve aquí es lo que hace Publicar: el backend
         calcula el mismo plan en los dos sitios, así que no se simula nada por esta parte. -->
    <f7-popup :opened="previaAbierta" @popup:closed="cerrarVistaPrevia">
      <f7-page>
        <f7-navbar :title="$t('festivos.vistaPrevia.titulo')">
          <f7-nav-right>
            <f7-link popup-close>{{ $t('cerrar') }}</f7-link>
          </f7-nav-right>
        </f7-navbar>

        <f7-block v-if="previaError" strong>
          <p class="aviso-sin-comunidad">{{ previaError }}</p>
          <f7-button @click="abrirVistaPrevia()">{{ $t('festivos.reintentar') }}</f7-button>
        </f7-block>

        <!-- El mismo olvido que el del respaldo, un nivel más arriba y de la #54: mientras el
             backend calcula el plan no hay `previa` ni `previaError`, así que la ventana se abría
             en blanco —solo la barra de arriba— y eso no se distingue de «no hay nada que
             publicar». Se dice que está calculando. -->
        <f7-block v-else-if="!previa" strong>
          <f7-preloader class="margin-right-half"></f7-preloader>
          {{ $t('festivos.vistaPrevia.calculando') }}
        </f7-block>

        <template v-else>
          <!-- La cifra NO puede llamarse `valor`: el editor de documentos declara `.valor` en una
               hoja global (rMediumEditor.vue) con borde discontinuo y un `~` delante y detrás para
               marcar los huecos de sus plantillas, y eso alcanzaba a estos contadores (JJCS,
               05/10/2026: «se pintan como ~2~, ~10~, ~1~»). El resto de la aplicación ya nombra
               estas cifras con prefijo —`metrica-valor`, `datos-valor`, `dos-cifras-valor`—. -->
          <f7-block strong class="contadores-previa">
            <div class="cifra">
              <span class="cifra-valor">{{ previa.calendarios }}</span>
              <span class="etiqueta">{{ $t('festivos.vistaPrevia.calendarios') }}</span>
            </div>
            <div class="cifra">
              <span class="cifra-valor">{{ previa.tareas }}</span>
              <span class="etiqueta">{{ $t('festivos.vistaPrevia.tareas') }}</span>
            </div>
            <div class="cifra" :class="{ 'con-choques': previa.choques > 0 }">
              <span class="cifra-valor">{{ previa.choques }}</span>
              <span class="etiqueta">{{ $t('festivos.vistaPrevia.choques') }}</span>
            </div>
          </f7-block>

          <f7-block class="pie-previa no-margin-top">
            {{ $t('festivos.vistaPrevia.yaExistentes') }}: {{ previa.yaExistentes }}
            <span v-if="previa.retiradas"> · {{ $t('festivos.vistaPrevia.retiradas') }}: {{ previa.retiradas }}</span>
          </f7-block>

          <!-- De dónde sale cada número. Va aquí, pegado a los contadores y antes de los choques,
               porque es lo que hace falta para leerlos: sin esto, «2 Calendarios» junto a un
               municipio que dice «Personas: 1» parecen dos verdades distintas (JJCS, 05/10/2026). -->
          <f7-block strong class="bloque-alcance">
            <p class="titulo-choque">{{ $t('festivos.vistaPrevia.alcanceTitulo') }}</p>
            <p class="linea-choque">{{ textoAlcance }}</p>
            <p class="linea-choque">{{ textoEscalera }}</p>
            <p v-if="personasSinCheck" class="linea-choque">
              {{ $t('festivos.vistaPrevia.alcanceSinCheck', { sinCheck: personasSinCheck }) }}
            </p>
            <p v-if="previa.personasSinMunicipio" class="linea-choque">
              {{ $t('festivos.vistaPrevia.alcanceSinMunicipio', { sinMunicipio: previa.personasSinMunicipio }) }}
            </p>
            <p class="nota-choque">{{ $t('festivos.vistaPrevia.alcanceUnidad') }}</p>
            <p class="nota-choque">{{ $t('festivos.vistaPrevia.alcanceManda') }}</p>
          </f7-block>

          <!-- Un bloque por choque de trabajo planificado: quién, qué día, qué festivo y qué
               tiene ya ese día. Y debajo, en gris, qué NO va a pasar. -->
          <f7-block v-for="(c, i) in choquesTrabajo" :key="'t' + i" strong class="bloque-choque">
            <p class="titulo-choque">{{ $t('festivos.vistaPrevia.trabajoPlanificado') }}</p>
            <p class="linea-choque">
              {{ c.nombre }} · {{ c.fecha }} {{ c.festivo }} ·
              {{ $t('festivos.vistaPrevia.tiene') }} «{{ c.detalle }}»
            </p>
            <p class="nota-choque">{{ $t('festivos.vistaPrevia.noSeBorra') }}</p>
          </f7-block>

          <f7-block v-if="!choquesVacaciones.length" strong class="bloque-ok">
            <p class="titulo-choque">{{ $t('festivos.vistaPrevia.vacacionesNinguna') }}</p>
            <p class="nota-choque">{{ $t('festivos.vistaPrevia.vacacionesExplicacion') }}</p>
          </f7-block>
          <f7-block v-else strong class="bloque-vacaciones">
            <p class="titulo-choque">{{ $t('festivos.vistaPrevia.vacaciones') }}</p>
            <p v-for="(c, i) in choquesVacaciones" :key="'v' + i" class="linea-choque">
              {{ c.nombre }} · {{ c.fecha }} {{ c.festivo }} · {{ c.detalle }}
            </p>
            <p class="nota-choque">{{ $t('festivos.vistaPrevia.vacacionesExplicacion') }}</p>
          </f7-block>

          <f7-block v-if="!previa.tareas && !previa.retiradas" strong>
            <p>{{ $t('festivos.vistaPrevia.sinNada') }}</p>
          </f7-block>

          <!-- El mismo aviso de respaldo que la pantalla, con la MISMA cuenta: es literalmente el
               mismo dato (`respaldo`), así que no puede decir otra cosa. Aquí es donde más
               importa, porque es el último sitio antes de escribir en los calendarios de las
               personas (#255). Avisa y no bloquea: el botón Publicar no mira nada de esto. -->
          <!-- La comprobación va por su propia petición, así que esta ventana puede abrirse antes
               de que llegue. Hasta la ronda 1 del PR #1799 ahí no se pintaba NADA: ni aviso, ni
               «cuadra», ni nada, y se podía pulsar Publicar creyendo que estaba comprobado. El
               estado en curso va primero porque esto es lo último que se ve antes de escribir en
               los calendarios de las personas: con una comprobación a medio hacer, lo honrado es
               decir que está a medio hacer y no enseñar la cuenta anterior como si fuera de ahora.
               Publicar sigue sin bloquearse por esto. -->
          <f7-block v-if="respaldoComprobando" strong class="bloque-respaldo-pendiente">
            <p class="titulo-choque">
              <f7-preloader class="margin-right-half"></f7-preloader>
              {{ $t('festivos.respaldo.comprobando') }}
            </p>
            <p class="nota-choque">{{ $t('festivos.respaldo.comprobandoEnVistaPrevia') }}</p>
          </f7-block>
          <f7-block v-else-if="respaldoCargado && hayAvisosDeRespaldo" strong class="bloque-respaldo-previa">
            <p class="titulo-choque">{{ $t('festivos.respaldo.enVistaPrevia') }}</p>
            <p class="linea-choque">
              {{ $t('festivos.respaldo.sinRespaldo') }}: {{ resumenRespaldo.sinRespaldo }}
              · {{ $t('festivos.respaldo.sinComprobar') }}: {{ resumenRespaldo.sinComprobar }}
              · {{ $t('festivos.respaldo.noCargados') }}: {{ resumenRespaldo.noCargadosCuenta }}
            </p>
            <p v-for="(c, i) in avisosDeRespaldo" :key="'r' + i" class="linea-choque">
              {{ c.fecha }} · {{ nombreDelAviso(c) }}
              <span v-if="c.municipioNombre">· {{ c.municipioNombre }}</span>
              · {{ $t('festivos.respaldo.etiquetas.' + c.tipo) }}
            </p>
            <p class="nota-choque">{{ $t('festivos.respaldo.noBloquea') }}</p>
          </f7-block>
          <f7-block v-else-if="respaldoCargado && resumenRespaldo.extracciones" strong class="bloque-ok">
            <p class="titulo-choque">{{ $t('festivos.respaldo.todoCuadra') }}</p>
          </f7-block>
          <!-- Sin ningún boletín leído no hay nada comprobado, así que tampoco se puede decir que
               cuadre: eso sería la pantalla afirmando algo que no ha mirado. -->
          <f7-block v-else-if="respaldoCargado" strong>
            <p class="nota-choque">{{ $t('festivos.respaldo.sinExtracciones', { anio: anio }) }}</p>
          </f7-block>
          <f7-block v-else-if="respaldoSinCargar" strong>
            <p class="nota-choque">{{ $t('festivos.respaldo.sinCargar') }}</p>
          </f7-block>

          <f7-block class="pie-previa">
            <p class="no-margin">{{ $t('festivos.vistaPrevia.pie') }}</p>
            <!-- El «i» de la ventana: el riesgo que se asume a propósito al escribir en los
                 calendarios, dicho donde lo lee quien pulsa Publicar y no solo en el código
                 (JJCS, 01/10/2026, punto 2). -->
            <p class="no-margin-bottom">
              <f7-icon f7="info_circle_fill" color="blue" size="14"></f7-icon>
              {{ $t('festivos.vistaPrevia.pieConcurrencia') }}
            </p>
          </f7-block>

          <f7-block class="acciones">
            <f7-button popup-close>{{ $t('cerrar') }}</f7-button>
            <!-- Sin nada que crear ni que retirar no hay nada que publicar: pulsarlo solo
                 dejaría un apunte en rh_audit de una publicación vacía. -->
            <f7-button
              fill
              color="green"
              :disabled="publicando || (!previa.tareas && !previa.retiradas)"
              @click="publicar"
            >{{ $t('festivos.publicar') }}</f7-button>
          </f7-block>
        </template>
      </f7-page>
    </f7-popup>
  </f7-page>
</template>
<style scoped>
.anio-actual { font-weight: bold; margin: 0 6px; }
.link-anio { padding: 0 4px; }
.contadores .contador {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  margin-right: 8px;
  color: #fff;
  font-weight: bold;
}
.contador-verde { background-color: #4cd964; }
.contador-ambar { background-color: #ff9500; }
.contador-rojo { background-color: #d32f2f; }
.contador-granate { background-color: #8e1b1b; }
.contador-morado { background-color: #7b1fa2; }
.contador-gris { background-color: #8e8e93; }
.contador-total { background-color: #22355b; }
.tabla-municipios { font-size: 13px; padding: 0 10px; }
.tabla-municipios .fila {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
}
.tabla-municipios .cabecera {
  font-weight: bold;
  color: #64748b;
  text-transform: uppercase;
  font-size: 11px;
  cursor: default;
}
.tabla-municipios .vacia { color: #8e8e93; cursor: default; }
.tabla-municipios .seleccionada { background-color: rgba(34, 53, 91, 0.1); }
.col-municipio { flex: 1 1 auto; }
.col-num { width: 52px; text-align: right; }
.col-estado { width: 140px; text-align: right; }
.chip-estado {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: bold;
  color: #fff;
  background-color: #8e8e93;
}
.chip-VALIDADO { background-color: #4cd964; }
/* Ámbar, como «pendiente de validar»: es algo que hay que hacer, no un error ni un final bueno. */
.chip-PUBLICADO_DESACTUALIZADO { background-color: #ff9500; }
.chip-PENDIENTE_VALIDAR { background-color: #ff9500; }
.chip-FALTAN_LOCALES { background-color: #d32f2f; }
.chip-FALTAN_COMPARTIDOS { background-color: #8e1b1b; }
.chip-SOBRAN_COMPARTIDOS { background-color: #7b1fa2; }
.chip-SOBRAN_LOCALES { background-color: #9c27b0; }
.chip-NO_CARGADO { background-color: #8e8e93; }
.acciones { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 8px; }
.accion-fila { margin-left: 8px; }
.pie-validacion { font-size: 12px; color: #64748b; margin: 0 0 4px; }
.pie-validacion .aviso-desfasado { color: #d32f2f; }
.aviso-sin-comunidad { font-size: 12px; color: #d32f2f; margin: 0 0 6px; }
.aviso-desactualizado {
  font-size: 12px;
  color: #8a5a00;
  background-color: #fff8e1;
  border-left: 4px solid #ff9500;
  padding: 6px 8px;
  margin: 0 0 6px;
}
/* Granate flojo, el color del «calendario incompleto»: es lo que hay que arreglar, y va pegado a
   los botones que lo arreglan, no arriba de la pantalla. */
.que-falta {
  font-size: 12px;
  color: #8e1b1b;
  background-color: #fdf1f1;
  border-left: 4px solid #8e1b1b;
  padding: 6px 8px;
  margin: 0 0 8px;
}
.que-falta-titulo { font-weight: bold; margin: 0 0 4px; }
.que-falta ul { margin: 0; padding-left: 18px; }
.que-falta li { margin-bottom: 3px; }
/* La publicación no es un problema del catálogo: se dice en gris, no en el granate del aviso. */
.que-falta-publicacion { margin: 6px 0 0; color: #64748b; }
.motivo-deshabilitado { font-size: 12px; color: #64748b; margin: 0 0 8px; }
.contador-azul { background-color: #2196f3; }
.contadores-previa { display: flex; gap: 18px; flex-wrap: wrap; }
.contadores-previa .cifra { display: flex; flex-direction: column; align-items: center; min-width: 110px; }
.contadores-previa .cifra-valor { font-size: 28px; font-weight: bold; color: #22355b; }
.contadores-previa .con-choques .cifra-valor { color: #d32f2f; }
.contadores-previa .etiqueta { font-size: 11px; color: #64748b; text-transform: uppercase; }
.bloque-choque { background-color: #fff8e1; border-left: 4px solid #ff9500; }
/* Azul «esto es información», no ámbar de aviso: explica los contadores, no alerta de nada. */
.bloque-alcance { background-color: #eef4fb; border-left: 4px solid #2196f3; }
.bloque-alcance .linea-choque { color: #22355b; }
.bloque-vacaciones { background-color: #fff3f3; border-left: 4px solid #d32f2f; }
.bloque-ok { background-color: #f1f9f1; border-left: 4px solid #4cd964; }
.titulo-choque { font-weight: bold; margin: 0 0 4px; }
.linea-choque { margin: 0 0 2px; font-size: 13px; }
.nota-choque { margin: 4px 0 0; font-size: 12px; color: #8e8e93; }
.pie-previa { font-size: 12px; color: #64748b; }
.genera-tarea { font-size: 11px; }
/* El aviso de respaldo va dentro del <li> de la fila, así que ocupa su propio renglón y se puede
   leer entero: el pie de la fila es de una línea y lo recortaría con puntos suspensivos. */
.aviso-respaldo {
  font-size: 11px;
  line-height: 1.35;
  white-space: normal;
  padding: 4px 16px 6px 16px;
  border-left: 4px solid #d32f2f;
  background-color: #fff3f3;
  color: #8e1b1b;
}
/* «No se puede comprobar» no es «está mal»: en gris, que es lo que dice que no se sabe. */
.aviso-SIN_RESPALDO_COMPROBABLE {
  border-left-color: #8e8e93;
  background-color: #f4f4f5;
  color: #4b5563;
}
.nota-respaldo { font-size: 11px; color: #8e8e93; margin: 2px 0 6px; }
.bloque-faltan-boletin {
  background-color: #f6f0fa;
  border-left: 4px solid #7b1fa2;
  padding: 6px 10px;
  margin: 0 0 8px;
}
.titulo-faltan { font-weight: bold; font-size: 12px; margin: 0 0 4px; color: #4a148c; }
.linea-faltan { font-size: 11px; line-height: 1.35; margin: 0 0 6px; }
.linea-faltan .ambito-faltan { color: #64748b; margin-left: 6px; }
.linea-faltan .texto-boletin { color: #64748b; font-style: italic; }
.bloque-respaldo-previa { background-color: #fff3f3; border-left: 4px solid #8e1b1b; }
/* En gris y no en granate: todavía no se sabe si hay algo que avisar, y pintarlo del color del
   aviso diría que sí. */
.bloque-respaldo-pendiente { background-color: #f4f4f4; border-left: 4px solid #8e8e93; }
.reintentar-respaldo { display: inline; color: #fff; text-decoration: underline; margin-left: 6px; }
.genera-si { color: #2e7d32; font-weight: bold; }
.genera-no { color: #8e8e93; }
.lista-festivos .item-title { font-size: 13px; }
.compartido { color: #b26a00; }
.festivo-no-aplica .item-title { color: #8e8e93; text-decoration: line-through; }
.aviso-no-aplica { margin: 0 16px 8px 46px; padding: 6px 8px; font-size: 12px; background-color: #f4f4f4; border-left: 4px solid #8e8e93; }
.accion-no-aplica-fila { margin: 0 16px 6px 46px; font-size: 12px; }
.accion-no-aplica { font-size: 12px; margin-left: 6px; }
.aviso-compartido { color: #b26a00; font-size: 13px; }
</style>
<script>
import Festivo from "./../../js/db/Festivo.js";
import FestivosAvisos from "./festivos-avisos.vue";
import Comunidad from "./../../js/db/Comunidad.js";
import carrilPeticiones from "./../../js/utils/carrilPeticiones.js";
import preloaderDeLaSesion from "./../../js/utils/preloaderDeLaSesion.js";
import cerrojoDeLaSesion from "./../../js/utils/cerrojoDeLaSesion.js";

/**
 * Las incidencias del catálogo para las que esta pantalla tiene explicación escrita
 * (`festivos.falta.*`). El backend manda la lista y el orden; esto solo evita que una incidencia
 * que aquí no se conozca se pinte como la clave de traducción sin traducir.
 */
const FALTAS_CON_TEXTO = ['NO_CARGADO', 'PENDIENTE_VALIDAR', 'FALTAN_COMPARTIDOS',
  'SOBRAN_COMPARTIDOS', 'FALTAN_LOCALES', 'SOBRAN_LOCALES'];

function resumenVacio() {
  return {
    noCargados: 0,
    pendientesValidar: 0,
    faltanCompartidos: 0,
    sobranCompartidos: 0,
    faltanLocales: 0,
    sobranLocales: 0,
    validados: 0,
    publicados: 0,
    desactualizados: 0,
    nacionales: 0,
    nacionalesValidados: 0,
    personasSinMunicipio: 0
  };
}

export default {
  components: {
    'festivos-avisos': FestivosAvisos
  },
  data: function() {
    return {
      anio: new Date().getFullYear(),
      municipios: [],
      resumen: resumenVacio(),
      puedeEditar: false,
      error403: false,
      // La tabla que se ve no es la de después de la última acción: o falló su carga o nunca
      // llegó. Se pinta como tal en vez de como «no hay municipios» (ver `cargar`).
      tablaSinCargar: false,
      // Hay una carga de la tabla en vuelo. Arranca en `true` porque la pantalla siempre pide la
      // tabla al entrar (`page:beforein`): con `false` habría un instante —el que va de crear el
      // componente a que arranque la petición— en el que ni se está cargando ni ha fallado nada, y
      // ahí la lista volvería a pintar el mensaje de vacío que es justo el defecto de la #262.
      cargando: true,
      // Una carga ha llegado bien y lo que hay en `municipios` y `resumen` es de verdad. Es lo que
      // separa «no hay nada» de «todavía no se sabe»: sin esto, los dos se cuentan igual.
      //
      // Es cosa distinta de `cargando`, y por eso son dos: al refrescar la tabla después de
      // validar o de guardar, lo que hay en pantalla sigue siendo real mientras llega lo nuevo, y
      // tapar los contadores en ese momento sería esconder cifras buenas.
      tablaCargada: false,
      filtro: '',
      seleccionado: null,
      // INE del último municipio pulsado. Se apunta en el mismo clic, antes de pedir su detalle:
      // `seleccionado` todavía es el anterior mientras la respuesta no llega, y refrescar la tabla
      // por él volvía a abrir el municipio viejo y a descartar el detalle del clic bueno.
      ineSeleccionado: null,
      festivos: [],
      comunidades: [],
      popupAbierto: false,
      edicion: {},
      // «No aplica en esta comunidad» (#1148): el nacional y la comunidad del municipio abierto.
      noAplica: { abierto: false, festivo: null, codComunidad: null, motivo: '', fuenteRef: '', fuenteUrl: '' },
      // Vista previa de la publicación. `previa` es lo que contestó el backend; null mientras
      // se pide, para no pintar contadores del cálculo anterior mientras llega el nuevo.
      previaAbierta: false,
      previa: null,
      previaError: null,
      publicando: false,
      // La comprobación del catálogo contra la extracción del boletín que cita (#255). Va por su
      // propia petición y su propio carril: es una lectura más —el catálogo, los apuntes de las
      // lecturas y la plantilla— y la tabla no puede quedarse sin pintar porque esta falle.
      respaldo: null,
      // Y por eso hay TRES estados, igual que con la tabla: «se está comprobando», «ha llegado» y
      // «no se pudo». Sin distinguirlos, un fallo de esta petición se vería como «ni un aviso»,
      // que es justo la afirmación que la pantalla no ha comprobado; y mientras está en vuelo —que
      // puede ser con la vista previa ya abierta, porque son dos peticiones independientes— el
      // silencio se lee igual de mal: nadie puede pulsar Publicar creyendo que está comprobado
      // (revisión CODEX del PR #1799).
      respaldoCargado: false,
      respaldoSinCargar: false,
      respaldoComprobando: false
    };
  },
  created: function() {
    // Un carril por cosa que se pide: las flechas de año lanzan una petición por pulsación y no
    // vuelven en orden, así que solo contesta la última de cada uno (ver carrilPeticiones). Fuera
    // de `data` porque no son estado que se pinte: no hace falta que Vue los observe.
    //
    // #472 · Y el carril es también quien tira lo pintado al pedir OTRA cosa y quien apaga el
    // «cargando»: dicho aquí una vez, ningún sitio que cambie el año o el municipio tiene que
    // acordarse de vaciar, y una respuesta descartada no puede apagar nada.
    const self = this;
    this.tabla = carrilPeticiones({
      // La tabla y los contadores son del año que se deja atrás: si la carga del nuevo falla se
      // quedarían en pantalla etiquetados con el año pedido, y pulsar una de esas filas abriría
      // el detalle de ese año para un municipio que quizá ni figure en él (revisión CODEX del PR
      // #1709). Y con la tabla vaciada tampoco se sabe nada del año nuevo: hasta que llegue su
      // respuesta, lo que toca decir es que está cargando, no que no hay nadie con municipio (#262).
      vaciar: function() {
        self.municipios = [];
        self.resumen = resumenVacio();
        self.tablaCargada = false;
      },
      cargando: function(si) { self.cargando = si; }
    });
    // El panel de la derecha y sus botones van contra `seleccionado`: conservar el detalle del
    // municipio anterior mientras llega el nuevo deja la pantalla enseñando uno y validando,
    // editando o borrando el otro. El del mismo municipio no se tira, porque su clave es la misma:
    // es el refresco de después de cada acción y parpadearía sin motivo.
    this.detalle = carrilPeticiones({
      vaciar: function() {
        self.seleccionado = null;
        self.festivos = [];
      }
    });
    this.vistaPreviaCarril = carrilPeticiones();
    this.respaldoCarril = carrilPeticiones();
  },
  computed: {
    // El indicador de la cabecera enlaza aquí con `?avisos=1`: quien pulsa viene a ver el detalle.
    vieneDelIndicador: function() {
      const ruta = this.$f7route;
      return !!(ruta && ruta.query && String(ruta.query.avisos) === '1');
    },
    // El «completo con N locales» que ya no cuadra con los locales validados: el backend deja de
    // darlo por bueno, así que la pantalla tiene que dejar volver a marcarlo.
    completoDesfasado: function() {
      var m = this.seleccionado;
      return !!(m && m.completoConLocales != null && m.completoConLocales !== m.localesValidados);
    },
    /**
     * Lo que le falla al municipio abierto, una línea por cosa y con sus números dentro.
     *
     * Los códigos y el orden los manda el backend (`incidencias`), que es el mismo sitio del que
     * salen las insignias de la cabecera: así la tarjeta no puede decir lo contrario que ellas.
     * Aquí solo se les pone el texto.
     *
     * Un código que esta pantalla no conozca se deja fuera: `$t` de una clave que no existe
     * devuelve la clave, así que sin este corte una incidencia nueva del backend pintaría
     * «festivos.falta.LO_QUE_SEA» en la ficha de un municipio.
     */
    queFalta: function() {
      const m = this.seleccionado;
      if (!m) return [];
      const self = this;
      return (m.incidencias || [])
        .filter(function(codigo) { return FALTAS_CON_TEXTO.indexOf(codigo) >= 0; })
        .map(function(codigo) {
          return {
            codigo: codigo,
            texto: self.$t('festivos.falta.' + codigo, {
              anio: self.anio,
              sinValidar: m.sinValidar || 0,
              compartidos: m.compartidos || 0,
              compartidosEsperados: m.compartidosEsperados || 0,
              nacionales: m.nacionales || 0,
              autonomicos: m.autonomicos || 0,
              localesQueAportan: m.localesQueAportan || 0,
              localesEsperados: m.localesEsperados || 0,
              // Las filas cargadas, además de los días que aportan: el texto nombra las dos
              // unidades porque no tienen por qué coincidir, y mezclarlas en una sola frase es lo
              // que hacía ilegible el «11 de los 12 días (12 nacionales y 2 autonómicos)».
              locales: m.locales || 0,
              localesValidados: m.localesValidados || 0
            })
          };
        });
    },
    /**
     * Cómo están sus calendarios, solo cuando la chapa no lo puede decir: con el catálogo
     * descuadrado gana el estado del catálogo y el eje de la publicación desaparecía de la
     * pantalla, así que borrar un nacional parecía haber deshecho lo publicado.
     *
     * `publicacion` nulo es «no se sabe» —lo devuelven así «Validar» y «Completo con locales»,
     * que no hacen la comparación—, y de eso no se afirma nada.
     */
    textoPublicacion: function() {
      const m = this.seleccionado;
      if (!m || !m.publicacion || !(m.incidencias || []).length) return null;
      if (m.publicacion === 'AL_DIA') return this.$t('festivos.publicacionAlDia');
      if (m.publicacion === 'DESACTUALIZADA') return this.$t('festivos.publicacionDesactualizada');
      return this.$t('festivos.publicacionNunca');
    },
    // El aviso de la corrección de un compartido, junto al botón Guardar (#1148). Solo en una
    // corrección: un alta nace sin validar y no le llega a nadie hasta que se valide.
    avisoCompartidoEdicion: function() {
      const e = this.edicion;
      if (!e || !e.id || e.municipiosQueLoComparten == null) return null;
      return this.textoCompartido(e, 'corregir');
    },
    // Cuántos festivos le aplican a este municipio: lo que «Validar» da por bueno de una vez. Las
    // tres columnas de su fila, que es de donde sale la cuenta del backend (`aplicables`).
    festivosQueAplican: function() {
      const m = this.seleccionado;
      if (!m) return 0;
      return (m.nacionales || 0) + (m.autonomicos || 0) + (m.locales || 0);
    },
    // Las comunidades del catálogo, más la del festivo que se está editando si ya no estuviera:
    // un festivo antiguo con un código retirado no puede cambiar de comunidad por abrirlo.
    opcionesComunidad: function() {
      var lista = this.comunidades.slice();
      var actual = this.edicion.codComunidad;
      var estaEnLista = lista.some(function(c) { return c.codigo === actual; });
      if (actual && !estaEnLista) lista.unshift({ codigo: actual, nombre: actual });
      return lista;
    },
    /**
     * Lo que la pantalla sabe del municipio que se está escribiendo en el formulario, venga del
     * municipio abierto o de cualquier otra fila de la tabla. Es el único sitio donde se mira:
     * comparando solo contra `seleccionado`, trasladar un festivo local a otro INE dejaba el
     * formulario sin desplegable de comunidad y sin poder indicarla
     * (revisión CODEX del PR #1709).
     */
    municipioDeLaEdicion: function() {
      var ine = this.edicion.municipioIne;
      if (!ine) return null;
      if (this.seleccionado && this.seleccionado.municipioIne === ine) return this.seleccionado;
      return this.municipios.filter(function(m) { return m.municipioIne === ine; })[0] || null;
    },
    // La comunidad de ese municipio no la fija el maestro: o ya no está en `municipios`, o está
    // sin comunidad. Ahí la pantalla es la única que puede decirla —al cargarle el primer local—
    // y también la única que puede corregirla después, porque lo que se ve deducido sale de sus
    // propios festivos (revisión CODEX del PR #1941).
    //
    // De un INE que no esté en la tabla no se sabe nada, así que el desplegable sale igual: el
    // backend ignora lo que se indique cuando el maestro sí la dice, y sin desplegable el
    // formulario solo podía mandar el municipio nuevo sin comunidad.
    comunidadLocalEditable: function() {
      var m = this.municipioDeLaEdicion;
      return this.edicion.ambito === 'LOCAL' && !!this.edicion.municipioIne
        && (!m || !m.comunidadFijadaEnMaestro);
    },
    // Y es obligatoria cuando además se sabe que ese municipio no tiene ninguna: sin ella su
    // calendario se quedaría sin los autonómicos para siempre, y el backend rechaza el alta
    // igual. Del INE desconocido no se exige nada: puede tener locales de los que el backend la
    // deduzca, y si no, el error que devuelve lo explica mejor que un aviso a ciegas.
    comunidadLocalRequerida: function() {
      var m = this.municipioDeLaEdicion;
      return this.comunidadLocalEditable && !!m && !m.codComunidad;
    },
    /**
     * Los contadores del respaldo, siempre con los cuatro números: la cabecera y la ventana de
     * vista previa tiran de ESTE objeto, así que no pueden decir cifras distintas. Mientras la
     * comprobación no ha llegado son ceros y no se pintan (ver `respaldoCargado`).
     */
    resumenRespaldo: function() {
      const r = this.respaldo || {};
      return {
        sinRespaldo: r.sinRespaldo || 0,
        sinComprobar: r.sinComprobar || 0,
        noCargadosCuenta: r.noCargadosCuenta || 0,
        extracciones: r.extracciones || 0
      };
    },
    // El aviso de cada festivo, por su id. Por el id y no por la fecha: dos municipios pueden
    // tener el mismo día cargado, y el panel de la derecha es de un municipio concreto.
    avisoPorFestivo: function() {
      const mapa = {};
      ((this.respaldo && this.respaldo.avisos) || []).forEach(function(aviso) {
        if (aviso && aviso.idFestivo) mapa[aviso.idFestivo] = aviso;
      });
      return mapa;
    },
    /**
     * Lo que el boletín trae y no está cargado, acotado al municipio abierto. A quién le toca cada
     * día lo resuelve el backend (`municipiosAfectados`) y no la pantalla: un nacional les toca a
     * todos y un autonómico a los de su comunidad, y deducirlo aquí sería la segunda versión de
     * «de qué comunidad es este municipio».
     */
    faltanDelBoletin: function() {
      const ine = this.seleccionado && this.seleccionado.municipioIne;
      if (!ine) return [];
      return ((this.respaldo && this.respaldo.noCargados) || []).filter(function(c) {
        return (c.municipiosAfectados || []).indexOf(ine) !== -1;
      });
    },
    // Cuántos avisos tiene el municipio abierto, contando los dos sentidos: es lo que decide si se
    // dice que no bloquean nada.
    avisosDelMunicipio: function() {
      const avisos = this.avisoPorFestivo;
      return this.festivos.filter(function(f) { return !!avisos[f.id]; }).length
        + this.faltanDelBoletin.length;
    },
    // Los avisos del año entero, los dos sentidos juntos, para la ventana de vista previa: ahí no
    // hay municipio abierto y lo que se enseña es todo lo que se va a publicar.
    avisosDeRespaldo: function() {
      const r = this.respaldo || {};
      return (r.avisos || []).concat(r.noCargados || []);
    },
    hayAvisosDeRespaldo: function() {
      return this.avisosDeRespaldo.length > 0;
    },
    // Los dos choques se pintan distinto y en sitios distintos de la ventana, así que se
    // separan aquí y no con un v-if dentro del mismo bucle.
    choquesTrabajo: function() {
      return ((this.previa && this.previa.detalleChoques) || [])
        .filter(function(c) { return c.tipo === 'TRABAJO_PLANIFICADO'; });
    },
    choquesVacaciones: function() {
      return ((this.previa && this.previa.detalleChoques) || [])
        .filter(function(c) { return c.tipo !== 'TRABAJO_PLANIFICADO'; });
    },
    // El ámbito de la ventana, dicho con palabras: desde la pantalla siempre se publica el año
    // entero (`municipioIne` va a null), pero el endpoint admite un municipio y entonces hay que
    // decirlo, o el mismo texto mentiría.
    textoAlcance: function() {
      if (!this.previa) return '';
      if (!this.previa.municipioIne) {
        return this.$t('festivos.vistaPrevia.alcanceTodos', { anio: this.previa.anio || this.anio });
      }
      return this.$t('festivos.vistaPrevia.alcanceMunicipio', {
        anio: this.previa.anio || this.anio,
        municipio: this.nombreDeMunicipio(this.previa.municipioIne)
      });
    },
    // La escalera que reconcilia la columna «Personas» de la tabla con el contador «Calendarios»:
    // plantilla con municipio → las que reciben festivos (check D-USR) → calendarios que cambian.
    textoEscalera: function() {
      if (!this.previa) return '';
      return this.$t('festivos.vistaPrevia.alcanceEscalera', {
        plantilla: this.previa.personasEnAmbito || 0,
        destinatarias: this.previa.personasDestinatarias || 0,
        calendarios: this.previa.calendarios || 0
      });
    },
    /**
     * Las personas con municipio a las que no se les escribe nada por no tener el check. Es la
     * resta de los dos números de arriba y es lo que RRHH puede arreglar: lo demás —que ya tengan
     * su T0083 puesta— no es nada que haya que hacer.
     */
    personasSinCheck: function() {
      if (!this.previa) return 0;
      return Math.max(0, (this.previa.personasEnAmbito || 0) - (this.previa.personasDestinatarias || 0));
    },
    /**
     * Lo que el buscador busca de verdad, normalizado una sola vez. Quien decida algo por la
     * búsqueda mira ESTO y no `filtro`: los dos no dicen lo mismo —escribir solo espacios deja
     * `filtro` con texto y la búsqueda vacía— y ahí es donde la pantalla se contradecía. Decía
     * «ningún municipio coincide» sin estar buscando nada, que es otra afirmación que no ha
     * comprobado (revisión CODEX del PR #1759).
     */
    filtroNormalizado: function() {
      return this.normalizarTexto(this.filtro);
    },
    municipiosFiltrados: function() {
      const q = this.filtroNormalizado;
      if (!q) return this.municipios;
      const self = this;
      return this.municipios.filter(function(m) {
        return self.normalizarTexto((m.municipioNombre || '') + ' ' + m.municipioIne).indexOf(q) !== -1;
      });
    }
  },
  methods: {
    /**
     * El nombre con el que se enseña un aviso de respaldo. Lo que el boletín trae y no está
     * cargado puede venir sin nombre: la identidad de un festivo es su día y a quién le toca, y el
     * servidor no inventa uno (#255, 4.ª ronda del PR #2071). Pintado tal cual, la fila se quedaba
     * en «2026-05-14 · » y parecía un nombre perdido por la pantalla; se dice que el boletín no lo
     * trae.
     */
    nombreDelAviso: function(c) {
      return (c && c.nombre) || this.$t('festivos.respaldo.sinNombre');
    },
    normalizarTexto: function(t) {
      return (t || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
    },
    es403: function(e) {
      return !!(e && (e.status == 403 || (e.xhr && e.xhr.status == 403)));
    },
    // El nombre del municipio si la tabla lo tiene cargado, y el INE si no: la ventana puede
    // abrirse sobre un municipio que el filtro de la tabla haya dejado fuera.
    nombreDeMunicipio: function(ine) {
      const fila = (this.municipios || []).filter(function(m) { return m.municipioIne === ine; })[0];
      return (fila && fila.municipioNombre) || ine;
    },
    /**
     * El enlace del boletín, solo si es navegable. El campo es texto libre y nadie lo valida al
     * guardarlo: metiéndolo en un `href` sin mirarlo, un «javascript:...» escrito ahí se
     * ejecutaría al pulsar. Lo que no sea http(s) se enseña como texto.
     */
    enlaceFuente: function(f) {
      var url = ((f && f.fuenteUrl) || '').trim();
      return /^https?:\/\//i.test(url) ? url : '';
    },
    tratarError: function(e) {
      if (this.es403(e)) {
        this.error403 = true;
      } else {
        this.$f7.dialog.alert(e && e.message ? e.message : this.$t('festivos.errorCarga'));
      }
    },
    cargar: function() {
      var self = this;
      // Se retira el error de la vez anterior al pedir, y el carril dice que está cargando en la
      // misma llamada: el enlace «Reintentar» tiene que cambiar la pantalla al pulsarlo, y dejar
      // los dos mensajes a la vez —«no se ha podido cargar» mientras se está cargando— es el mismo
      // defecto de la #262 visto del revés.
      self.tablaSinCargar = false;
      // Y el desglose del aviso semanal (#1210), también en cada pasada: validar o corregir un
      // municipio es justo lo que lo cambia, y el indicador de la cabecera se entera por aquí.
      if (self.$refs.avisos) self.$refs.avisos.cargar();
      // En la misma pasada que la tabla: los avisos de respaldo son del año que se está mirando y
      // cambian con cualquier alta, corrección o borrado, que es justo lo que llama aquí.
      self.cargarRespaldo();
      self.tabla.lanzar(Festivo.municipios(self.anio), {
        // #472 · de qué año es: con otro año pintado, el carril vacía la tabla al lanzar.
        clave: [self.anio],
        exito: function(res) {
          res = res || {};
          self.error403 = false;
          self.tablaCargada = true;
          self.tablaSinCargar = false;
          self.municipios = res.municipios || [];
          self.resumen = {
            noCargados: res.noCargados || 0,
            pendientesValidar: res.pendientesValidar || 0,
            faltanCompartidos: res.faltanCompartidos || 0,
            sobranCompartidos: res.sobranCompartidos || 0,
            faltanLocales: res.faltanLocales || 0,
            sobranLocales: res.sobranLocales || 0,
            validados: res.validados || 0,
            publicados: res.publicados || 0,
            desactualizados: res.desactualizados || 0,
            nacionales: res.nacionales || 0,
            nacionalesValidados: res.nacionalesValidados || 0,
            personasSinMunicipio: res.personasSinMunicipio || 0
          };
          self.puedeEditar = res.puedeValidar === true;
          if (self.ineSeleccionado) {
            self.seleccionar(self.ineSeleccionado);
          }
        },
        // Validar, corregir, borrar o crear acaban llamando a `cargar()`: si falla este GET, lo
        // que queda en pantalla es el estado de ANTES de la acción —el festivo recién borrado,
        // con sus botones— y la tabla seguiría pintando «pendiente de validar» un municipio
        // recién validado. Se vacía todo y se dice que no se pudo cargar, con un enlace para
        // reintentar: sin tabla que respalde lo que se ve, operar desde ahí sería hacerlo a
        // ciegas (revisión CODEX del PR #1709).
        limpiar: function() {
          self.seleccionado = null;
          self.festivos = [];
          self.ineSeleccionado = null;
          self.municipios = [];
          self.resumen = resumenVacio();
          self.tablaCargada = false;
          self.tablaSinCargar = true;
        },
        fallo: function(e) { self.tratarError(e); }
      });
    },
    /**
     * La comprobación del catálogo contra los boletines leídos (#255). Si falla, NO se abre
     * ningún diálogo y no se toca la tabla: es un aviso de más, y perder el aviso no puede dejar
     * sin pintar la pantalla. Lo que sí se dice es que no se ha podido comprobar, para que un
     * «Sin respaldo: 0» no parezca un catálogo revisado.
     *
     * Y se dice ANTES de pedirla que se está comprobando, por lo mismo: entre el clic y la
     * respuesta la pantalla no sabe nada del respaldo, y no decirlo deja que se lea como
     * comprobado. En una recarga —después de validar, corregir o borrar— los números anteriores
     * se quedan, igual que los contadores de la tabla (ver `tablaCargada`): son los de la última
     * comprobación de verdad y taparlos sería esconder cifras buenas. Lo que no se puede es
     * darlos por vigentes mientras se recalculan, y eso es lo que dice `respaldoComprobando`.
     */
    cargarRespaldo: function() {
      const self = this;
      self.respaldoSinCargar = false;
      // #663 · El cerrojo de la sesión: el carril no llama a nadie con la cancelación de cambiar de
      // cuenta, así que ni `exito` ni `limpiar` lo soltarían y se quedaría «comprobando».
      cerrojoDeLaSesion(self, 'respaldoComprobando');
      self.respaldoCarril.lanzar(Festivo.respaldo(self.anio), {
        exito: function(res) {
          self.respaldo = res || null;
          self.respaldoCargado = !!res;
          self.respaldoSinCargar = !res;
          self.respaldoComprobando = false;
        },
        limpiar: function() {
          self.respaldo = null;
          self.respaldoCargado = false;
          self.respaldoSinCargar = true;
          self.respaldoComprobando = false;
        },
        // Sin `fallo`: el 403 y los errores de carga los cuenta ya la tabla, que pide lo mismo con
        // el mismo gate. Un segundo diálogo por la misma causa solo se cierra dos veces.
        // Su respuesta ya no es la del año que se está mirando, así que no pinta nada. Y
        // `respaldoComprobando` se queda como está a propósito: hay otra petición en vuelo —la
        // que sí manda—, y apagarlo aquí diría «comprobado» sin haber llegado ninguna respuesta.
        descartada: function() { /* la vigente es la que decide */ }
      });
    },
    cambiarAnio: function(delta) {
      this.anio = this.anio + delta;
      this.seleccionado = null;
      this.ineSeleccionado = null;
      this.festivos = [];
      // La tabla y los contadores del año que se deja atrás los tira su carril al pedir el nuevo
      // (#472, ver `created`).
      //
      // El detalle y la vista previa que estuvieran en vuelo son de otro año: que no vuelva a
      // abrir la ficha ni a pintar unos contadores que ya no son de este año.
      this.detalle.invalidar();
      this.vistaPreviaCarril.invalidar();
      // Los avisos de respaldo también son del año que se deja atrás: si se quedaran, la cabecera
      // y la ventana de vista previa contarían los del año anterior con el año nuevo escrito.
      this.respaldoCarril.invalidar();
      this.respaldo = null;
      this.respaldoCargado = false;
      this.cargar();
    },
    seleccionar: function(municipioIne) {
      var self = this;
      self.ineSeleccionado = municipioIne;
      // Para nombrar la comunidad en «No aplica en…»: decir «No aplica en 10» no lo entiende nadie.
      self.cargarComunidades();
      self.detalle.lanzar(Festivo.detalle(municipioIne, self.anio), {
        // #472 · de qué municipio y de qué año es. Con otro pintado, el carril tira el detalle al
        // lanzar; con el mismo lo deja, que es el refresco de después de cada acción y si falla
        // lo vacía `limpiar` igual (revisión CODEX del PR #1709). Ver `created`.
        clave: [municipioIne, self.anio],
        exito: function(res) {
          res = res || {};
          self.seleccionado = res.municipio || null;
          // Sin municipio en la respuesta no hay nada que refrescar: dejarlo apuntado haría que
          // cada recarga de la tabla volviera a pedir lo mismo.
          if (!self.seleccionado) self.ineSeleccionado = null;
          self.festivos = res.festivos || [];
        },
        // También cuando es el mismo municipio: ese es el refresco de después de validar,
        // corregir o borrar, y si falla solo él lo que queda en pantalla es el estado de ANTES de
        // la acción —el festivo recién borrado seguía en la lista, con sus botones—
        // (revisión CODEX del PR #1709).
        limpiar: function() {
          self.seleccionado = null;
          self.festivos = [];
          self.ineSeleccionado = null;
        },
        fallo: function(e) { self.tratarError(e); }
      });
    },
    /**
     * Abre la ventana y pide el plan. Se publica el año entero y no el municipio abierto: la
     * ventana cuenta calendarios y choques de toda la plantilla, que es lo que hay que mirar
     * antes de escribir en los calendarios de todo el mundo.
     *
     * <p>Por su carril, como las demás cargas: cerrar la ventana, cambiar de año y volver a
     * abrirla deja dos peticiones en vuelo, y la del año viejo llegando la última pintaba sus
     * contadores y sus choques mientras el botón Publicar seguía apuntando al año nuevo. Quien
     * pulsara estaría confirmando un año mirando las cifras de otro (revisión CODEX del PR
     * #1719).</p>
     */
    abrirVistaPrevia: function() {
      const self = this;
      self.previa = null;
      self.previaError = null;
      self.previaAbierta = true;
      self.vistaPreviaCarril.lanzar(Festivo.vistaPrevia(self.anio, null), {
        exito: function(res) {
          // Una vista previa sin nada que hacer devuelve contadores a cero, no null: el `|| {}`
          // es para que la ventana no se quede en blanco si la respuesta viene vacía.
          self.previa = res || { calendarios: 0, tareas: 0, choques: 0, yaExistentes: 0,
            retiradas: 0, detalleChoques: [] };
        },
        limpiar: function() { self.previa = null; },
        fallo: function(e) {
          self.previaError = (e && e.message) || self.$t('festivos.vistaPrevia.error');
        }
      });
    },
    cerrarVistaPrevia: function() {
      // Lo que quede en vuelo ya no tiene ventana donde pintarse: que no reviva `previa` justo
      // cuando se acaba de cerrar.
      this.vistaPreviaCarril.invalidar();
      this.previaAbierta = false;
      this.previa = null;
      this.previaError = null;
    },
    publicar: function() {
      const self = this;
      const app = self.$f7;
      // #663 · La espera y el cerrojo de la sesión: si la sesión cambia en vuelo, la frontera calla
      // el `then` que los soltaba.
      cerrojoDeLaSesion(self, 'publicando');
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      // Lo que contesta el publicado manda sobre cualquier vista previa en vuelo: son los
      // contadores de lo que se acaba de escribir, no los de lo que se iba a escribir.
      self.vistaPreviaCarril.invalidar();
      Festivo.publicar(self.anio, null, null)
        .then(function(res) {
          res = res || {};
          self.previaAbierta = false;
          self.previa = null;
          app.dialog.alert(self.resumenPublicado(res));
          // La tabla cambia de estado al publicar («Validado · publicado»), así que se recarga.
          self.cargar();
        })
        .catch(function(e) {
          app.dialog.alert((e && e.message) || self.$t('festivos.vistaPrevia.errorPublicar'));
        })
        .then(function() {
          self.publicando = false;
          espera.cerrar();
        });
    },
    /**
     * Lo que se le dice a quien acaba de publicar, y sale todo de la respuesta: son los números de
     * lo que se ha escrito, no los contadores de la ventana (JJCS, 01/10/2026, punto 1).
     *
     * <p>Las dos frases de detalle solo salen si tienen algo que contar: en una primera publicación
     * no hay días movidos ni vacaciones tocadas, y enseñar ceros solo tapa el número que importa.</p>
     */
    resumenPublicado: function(res) {
      const lineas = [this.$t('festivos.vistaPrevia.publicado', {
        creadas: res.creadas || 0,
        retiradas: res.retiradas || 0,
        // El mismo contador que la ventana enseña arriba y en la misma unidad: es lo único que
        // permite comprobar que los dos números coinciden, que es el criterio de esta tarea.
        calendarios: res.calendarios || 0
      })];
      if (res.movidas || res.rehechas) {
        lineas.push(this.$t('festivos.vistaPrevia.publicadoMovimientos', {
          movidas: res.movidas || 0,
          rehechas: res.rehechas || 0
        }));
      }
      if (res.diasDevueltos || res.diasRepuestos) {
        lineas.push(this.$t('festivos.vistaPrevia.publicadoVacaciones', {
          diasDevueltos: res.diasDevueltos || 0,
          diasRepuestos: res.diasRepuestos || 0
        }));
      }
      return lineas.join(' ');
    },
    /**
     * Lo que acaba de pasar, con palabras. Las dos acciones de la tarjeta pueden volver sin error
     * y dejar el estado del municipio igual que estaba —validar cuando no quedaba nada pendiente,
     * o marcar los locales cuando lo que descuadra son los compartidos—, y entonces la pantalla
     * recargaba y no decía nada: quien lo pulsó solo veía que no pasaba nada y volvía a pulsarlo
     * (abmsimecal-cloud, preview-2, 13/10/2026).
     *
     * El texto sale de la fila que devuelve la acción, que es la de después de escribir.
     */
    avisarDeLaAccion: function(hecho, fila) {
      const app = this.$f7;
      const nombre = (fila && fila.municipioNombre) || (this.seleccionado && this.seleccionado.municipioNombre);
      const lineas = [hecho];
      if (fila) {
        lineas.push((fila.incidencias || []).length
          ? this.$t('festivos.sigueIncompleto', { municipio: nombre })
          : this.$t('festivos.calendarioCuadrado', { municipio: nombre }));
      }
      app.toast.create({ text: lineas.join(' '), closeTimeout: 6000, closeButton: true }).open();
    },
    validar: function() {
      const self = this;
      const app = self.$f7;
      // Lo que había pendiente ANTES de validar: la fila que contesta ya viene con cero, así que
      // es lo único que permite decir si el botón ha hecho algo o no.
      const pendientes = self.seleccionado.sinValidar || 0;
      const nombre = self.seleccionado.municipioNombre;
      app.dialog.confirm(
        self.$t('festivos.confirmarValidar', { municipio: nombre, anio: self.anio }),
        self.$t('festivos.validar'),
        function() {
          const espera = preloaderDeLaSesion(self);
          espera.mostrar();
          Festivo.validar(self.anio, self.seleccionado.municipioIne, null)
            .then(function(fila) {
              self.avisarDeLaAccion(pendientes
                ? self.$t('festivos.validado', { validados: pendientes, municipio: nombre })
                : self.$t('festivos.validadoSinCambios', { municipio: nombre }), fila);
              self.cargar();
            })
            .catch(function(e) { self.tratarError(e); })
            .then(function() { espera.cerrar(); });
        }
      );
    },
    // El motivo es obligatorio: si no lo escriben, el backend lo rechaza igual.
    marcarCompleto: function(deshacer) {
      const self = this;
      const app = self.$f7;
      app.dialog.prompt(
        deshacer ? self.$t('festivos.motivoDeshacer') : self.$t('festivos.motivoCompleto'),
        function(motivo) {
          if (!motivo) {
            app.dialog.alert(self.$t('festivos.faltaMotivo'));
            return;
          }
          const espera = preloaderDeLaSesion(self);
          espera.mostrar();
          Festivo.completoLocales(self.anio, self.seleccionado.municipioIne, motivo, deshacer)
            .then(function(fila) {
              self.avisarDeLaAccion(deshacer
                ? self.$t('festivos.marcadoDeshecho')
                : self.$t('festivos.marcadoCompleto', {
                  locales: (fila && fila.completoConLocales != null) ? fila.completoConLocales : 0
                }), fila);
              self.cargar();
            })
            .catch(function(e) { self.tratarError(e); })
            .then(function() { espera.cerrar(); });
        }
      );
    },
    // El desplegable de comunidades se pide la primera vez que se abre el formulario: la tabla
    // se recarga sola tras cada acción y no tiene por qué arrastrar esta petición.
    cargarComunidades: function() {
      var self = this;
      if (self.comunidades.length) return;
      Comunidad.getList({ limit: 100, order: 'codigo', projection: ['codigo', 'nombre'] })
        .then(function(res) { self.comunidades = (res && res.list) || []; })
        .catch(function() { self.comunidades = []; });
    },
    // La comunidad que hubiera era la del municipio anterior. Se suelta al cambiar el código: el
    // backend ahora se queda con la del formulario cuando el maestro no dice nada del municipio,
    // así que arrastrarla le colgaría a otro municipio una comunidad que no es la suya.
    cambiarMunicipioIne: function(valor) {
      if (valor !== this.edicion.municipioIne) this.edicion.codComunidad = null;
      this.edicion.municipioIne = valor;
    },
    abrirAlta: function(municipioIne) {
      this.cargarComunidades();
      this.edicion = {
        id: null,
        fecha: this.anio + '-01-01',
        nombre: '',
        ambito: municipioIne ? 'LOCAL' : 'NACIONAL',
        codComunidad: null,
        municipioIne: municipioIne || null,
        fuenteRef: '',
        fuenteUrl: '',
        motivo: ''
      };
      this.popupAbierto = true;
    },
    abrirEdicion: function(festivo) {
      this.cargarComunidades();
      // Las claves que el festivo puede no traer se ponen antes: en Vue 2 una propiedad añadida
      // después no es reactiva y el desplegable de comunidad se quedaría sin actualizar el modelo.
      this.edicion = Object.assign({ motivo: '', codComunidad: null, municipioIne: null }, festivo);
      this.popupAbierto = true;
    },
    guardarFestivo: function() {
      var self = this;
      var app = self.$f7;
      var datos = self.edicion;
      if (!datos.fecha || !datos.nombre) {
        app.dialog.alert(self.$t('festivos.faltanDatos'));
        return;
      }
      // `min`/`max` acotan el selector, pero la fecha se puede teclear igual y el backend guarda
      // el año que venga en ella. Aquí se corta, que es donde se sabe qué año está mirando quien
      // escribe: para mover un festivo a otro año se cambia de año y se da de alta allí.
      if (String(datos.fecha).slice(0, 4) !== String(self.anio)) {
        app.dialog.alert(self.$t('festivos.fechaDeOtroAnio', { anio: self.anio }));
        return;
      }
      // El backend la exige igual; decirlo aquí ahorra el viaje y el mensaje largo.
      if (self.comunidadLocalRequerida && !datos.codComunidad) {
        app.dialog.alert(self.$t('festivos.faltaComunidadLocal'));
        return;
      }
      const enviar = function(confirmoCompartido) {
        const espera = preloaderDeLaSesion(self);
        espera.mostrar();
        const peticion = datos.id
          ? Festivo.actualizar(datos.id, Object.assign({}, datos, { confirmoCompartido: confirmoCompartido }))
          : Festivo.crear(datos);
        peticion
          .then(function() {
            self.popupAbierto = false;
            self.cargar();
          })
          .catch(function(e) {
            app.dialog.alert(e && e.message ? e.message : self.$t('festivos.errorGuardar'));
          })
          .then(function() { espera.cerrar(); });
      };
      // La corrección de un compartido se confirma expresamente, con los mismos números que dice
      // el aviso encima del botón: el backend la rechaza sin esa confirmación (#1148).
      if (self.avisoCompartidoEdicion) {
        app.dialog.confirm(self.avisoCompartidoEdicion, self.$t('festivos.compartido.tituloCorregir'),
          function() { enviar(true); });
        return;
      }
      enviar(false);
    },
    /**
     * Borrar un festivo. Si es compartido —nacional o autonómico— antes de pedir el motivo se dice
     * a cuántos municipios y personas llega y se pide confirmación expresa: el 30/10/2026 se borró
     * el 12 de octubre desde la ficha de Alicante y desapareció de los 24 municipios (#1148).
     */
    borrar: function(festivo) {
      const self = this;
      const app = self.$f7;
      const pedirMotivo = function(confirmoCompartido) {
        app.dialog.prompt(self.$t('festivos.motivoBorrado'), function(motivo) {
          const espera = preloaderDeLaSesion(self);
          espera.mostrar();
          Festivo.borrar(festivo.id, motivo, confirmoCompartido)
            .then(function() { self.cargar(); })
            .catch(function(e) { self.tratarError(e); })
            .then(function() { espera.cerrar(); });
        });
      };
      if (festivo.municipiosQueLoComparten == null) {
        pedirMotivo(false);
        return;
      }
      app.dialog.confirm(self.textoCompartido(festivo, 'borrar'), self.$t('festivos.compartido.tituloBorrar'),
        function() { pedirMotivo(true); });
    },
    /**
     * Lo que se dice antes de tocar un registro compartido, con sus números: a cuántos municipios
     * y personas llega, que el cambio les llega a todos, si es un día ya pasado y, si es un
     * nacional, que lo que quizá se busca es «No aplica en…».
     */
    textoCompartido: function(festivo, accion) {
      const m = this.seleccionado;
      const partes = [this.$t('festivos.compartido.' + accion, {
        festivo: festivo.fecha + ' · ' + festivo.nombre,
        ambito: this.$t('festivos.ambitos.' + festivo.ambito).toLowerCase(),
        municipios: festivo.municipiosQueLoComparten || 0,
        personas: festivo.personasQueLoComparten || 0,
        municipio: m ? m.municipioNombre : ''
      })];
      if (festivo.fechaPasada) partes.push(this.$t('festivos.compartido.pasado'));
      if (festivo.ambito === 'NACIONAL' && m && m.codComunidad) {
        partes.push(this.$t('festivos.compartido.sugerirNoAplica', { comunidad: this.nombreComunidad(m.codComunidad) }));
      }
      return partes.join(' ');
    },
    // El nombre del maestro de comunidades, o el código si todavía no ha llegado.
    nombreComunidad: function(codigo) {
      if (!codigo) return '';
      const comunidad = this.comunidades.filter(function(c) { return c.codigo === codigo; })[0];
      return comunidad && comunidad.nombre ? comunidad.nombre : codigo;
    },
    abrirNoAplica: function(festivo) {
      this.noAplica = {
        abierto: true,
        festivo: festivo,
        codComunidad: this.seleccionado.codComunidad,
        motivo: '',
        fuenteRef: '',
        fuenteUrl: ''
      };
    },
    // Motivo y fuente obligatorios, como en el backend: se dice aquí para ahorrar el viaje.
    guardarNoAplica: function() {
      const self = this;
      const app = self.$f7;
      const datos = self.noAplica;
      if (!datos.motivo || !datos.motivo.trim()) {
        app.dialog.alert(self.$t('festivos.faltaMotivo'));
        return;
      }
      if (!(datos.fuenteRef || '').trim() && !(datos.fuenteUrl || '').trim()) {
        app.dialog.alert(self.$t('festivos.noAplica.faltaFuente'));
        return;
      }
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      Festivo.noAplicaEn(datos.festivo.id, {
        codComunidad: datos.codComunidad,
        motivo: datos.motivo,
        fuenteRef: datos.fuenteRef,
        fuenteUrl: datos.fuenteUrl
      })
        .then(function() {
          self.noAplica.abierto = false;
          self.avisarDeLaAccion(self.$t('festivos.noAplica.hecho', {
            festivo: datos.festivo.nombre, comunidad: self.nombreComunidad(datos.codComunidad) }));
          self.cargar();
        })
        .catch(function(e) { app.dialog.alert(e && e.message ? e.message : self.$t('festivos.errorGuardar')); })
        .then(function() { espera.cerrar(); });
    },
    volverAAplicar: function(festivo) {
      const self = this;
      const app = self.$f7;
      const exclusion = festivo.noAplicaAqui;
      const comunidad = exclusion.comunidadNombre || exclusion.codComunidad;
      app.dialog.prompt(self.$t('festivos.noAplica.motivoDeshacer', { festivo: festivo.nombre, comunidad: comunidad }),
        function(motivo) {
          if (!motivo) {
            app.dialog.alert(self.$t('festivos.faltaMotivo'));
            return;
          }
          const espera = preloaderDeLaSesion(self);
          espera.mostrar();
          Festivo.vuelveAAplicar(festivo.id, exclusion.codComunidad, motivo)
            .then(function() {
              self.avisarDeLaAccion(self.$t('festivos.noAplica.deshecho', { festivo: festivo.nombre, comunidad: comunidad }));
              self.cargar();
            })
            .catch(function(e) { self.tratarError(e); })
            .then(function() { espera.cerrar(); });
        });
    }
  }
};
</script>
